import omit from 'lodash/omit';

import { normalizeAddOnAnchor } from './addOnAnchor.js';

/** Where a piece is placed. Each viewer resolves the types it can draw. */
export interface AddOnAnchor {
    type: string;
    page?: number;
    point?: { x: number | string; y: number | string };
    locator?: { href?: string };
    [key: string]: unknown;
}

/**
 * A record as the store holds it. Only the fields named below are read
 * here, and every other field the host sends (title, body, type) travels
 * through untouched. The multi-word fields take the write contract's names
 * (byLine, bodyFormat, imageUrl, imageCaption). normalizeAddOn renames the
 * listing's snake_case fields and drops its legacy ones.
 */
export interface AddOn {
    id: string | number;
    status?: string;
    anchor?: AddOnAnchor;
    [key: string]: unknown;
}

/** The one status a reader who is not an editor is shown. */
export const ADD_ON_PUBLISHED = 'published';

/** Written but not published: only an editor is shown one. */
export const ADD_ON_DRAFT = 'draft';

/**
 * Admits the one status it knows, so a value the review lifecycle
 * gains later stays unpublished until this module learns it.
 */
export const isAddOnPublished = (addOn: AddOn): boolean => addOn.status === ADD_ON_PUBLISHED;

/** The form's own draft carries the same status field, so it answers here too. */
export const isAddOnDraft = (addOn?: { status?: string | null } | null): boolean => addOn?.status === ADD_ON_DRAFT;

/**
 * Ids reach the reader as numbers from the PDF payload and as strings
 * from fixtures and the URL, so they only ever compare as strings.
 */
export const hasAddOnId = (addOn: AddOn, id: string | number | null | undefined): boolean =>
    String(addOn.id) === String(id);

/** A piece saved with no host to keep it takes an id under this prefix, and exists only in this reader. */
export const LOCAL_ADD_ON_ID_PREFIX = 'local-';

export const isLocalAddOnId = (id: string | number | null | undefined): boolean =>
    String(id).startsWith(LOCAL_ADD_ON_ID_PREFIX);

/** The first record that id names, whichever way either spelled it. */
export const findAddOnById = (addOns: AddOn[], id: string | number | null | undefined): AddOn | null =>
    addOns.find(addOn => hasAddOnId(addOn, id)) ?? null;

// The listing spells these in snake_case, the record as the write contract does.
const RECORD_NAMES = {
    by_line: 'byLine',
    body_format: 'bodyFormat',
    image_url: 'imageUrl',
    image_caption: 'imageCaption',
} as const;

const RECORD_NAME_PAIRS = Object.entries(RECORD_NAMES);

/** The listing's names replaced by the record's own. */
function withRecordNames(addOn: AddOn): AddOn {
    if (!RECORD_NAME_PAIRS.some(([listingName]) => listingName in addOn)) return addOn;

    const renamed: AddOn = { ...addOn };
    for (const [listingName, recordName] of RECORD_NAME_PAIRS) {
        if (!(listingName in renamed)) continue;
        renamed[recordName] = renamed[listingName];
        delete renamed[listingName];
    }
    return renamed;
}

// The anchor places every piece and imageUrl names its image, so the listing's
// legacy page fields and the deprecated aliases it adds stay out of the record.
const LEGACY_FIELDS = ['start_page', 'end_page', 'location_in_start_page', 'startPage', 'endPage', 'thumbnail'];

function withoutLegacyFields(addOn: AddOn): AddOn {
    if (!LEGACY_FIELDS.some(field => field in addOn)) return addOn;

    return omit(addOn, LEGACY_FIELDS);
}

/**
 * One shape for every record the store holds. A record written before
 * the review lifecycle carries no status and was always visible, so
 * it publishes. Any other value is left to the visibility gate.
 */
export function normalizeAddOn(addOn: AddOn | null | undefined): AddOn | null | undefined {
    if (!addOn) return addOn;

    const anchored = withoutLegacyFields(normalizeAddOnAnchor(withRecordNames(addOn)));
    if (anchored.status != null) return anchored;

    return { ...anchored, status: ADD_ON_PUBLISHED };
}
